import {FC, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {ValidationErrorModal} from '../../../../_gori/partials/widgets'
import {MetricCard} from './MetricCard'
import {ShipmentTrends} from './ShipmentTrends'
import {ShipmentTrendsModal} from '../modals/ShipmentTrendsModal'
import {ShipmentMap} from './ShipmentMap'
import {OrganizationOverviewSkeleton} from './OrganizationOverviewSkeleton'
import OrganizationService from '../core/_requests'
import {EMPTY_ANALYTICS_METRIC} from '../core/_const'
import {
  buildOrganizationAnalyticsPayload,
  OrganizationAnalyticsFilters,
} from '../helpers/organizationAnalyticsHelpers'
import {useOrganizationBranches} from '../providers/OrganizationBranchesProvider'

const OrganizationOverview: FC<OrganizationAnalyticsFilters> = ({
  accountIds,
  timeRange,
  isCustomRange,
  dateRange,
}) => {
  const intl = useIntl()
  const {isBranchesReady} = useOrganizationBranches()
  const {newCancelToken, isCancel} = useCancelToken()
  const [showShipmentTrendsModal, setShowShipmentTrendsModal] = useState(false)
  const [totalShipments, setTotalShipments] = useState(EMPTY_ANALYTICS_METRIC)
  const [totalSpend, setTotalSpend] = useState(EMPTY_ANALYTICS_METRIC)
  const [isLoading, setIsLoading] = useState(true)
  const [validationErrors, setValidationErrors] = useState<any>()
  const analyticsFilters = useMemo(
    () => ({accountIds, timeRange, isCustomRange, dateRange}),
    [accountIds, dateRange, isCustomRange, timeRange]
  )

  useEffect(() => {
    if (!isBranchesReady) {
      return
    }

    setIsLoading(true)

    if (!accountIds.length) {
      setTotalShipments(EMPTY_ANALYTICS_METRIC)
      setTotalSpend(EMPTY_ANALYTICS_METRIC)
      setIsLoading(false)
      return
    }

    const loadOverviewMetrics = async () => {
      const config = {cancelToken: newCancelToken()}

      try {
        const [shipments, spend] = await Promise.all([
          OrganizationService.getTotalShipments(
            buildOrganizationAnalyticsPayload(analyticsFilters),
            config
          ),
          OrganizationService.getTotalSpend(
            buildOrganizationAnalyticsPayload(analyticsFilters),
            config
          ),
        ])

        if (shipments) {
          setTotalShipments(shipments)
        }

        if (spend) {
          setTotalSpend(spend)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setIsLoading(false)
      }
    }

    loadOverviewMetrics()
  }, [accountIds.length, analyticsFilters, isBranchesReady, isCancel, newCancelToken])

  const showSkeleton = !isBranchesReady || isLoading

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => setValidationErrors(undefined)}
          response={validationErrors}
        />
      )}
      <ShipmentTrendsModal
        show={showShipmentTrendsModal}
        handleClose={() => setShowShipmentTrendsModal(false)}
        {...analyticsFilters}
      />
      <div className='card table-content rounded-top-left-0-dt'>
        <div className='table-content--inset-inner'>
          <div className='card-body'>
            <div className='organizations-overview gap-5'>
              <div className='d-flex flex-column align-items-start organizations-overview__left-side'>
                {showSkeleton ? (
                  <OrganizationOverviewSkeleton />
                ) : (
                  <>
                    <div className='organizations-overview__shipment-metrics col-12'>
                      <div className='organizations-overview__metrics-wrapper'>
                        <MetricCard
                          className='w-100'
                          title={intl.formatMessage({id: 'TOTAL_SHIPMENTS'})}
                          breakoutTitle={intl.formatMessage({id: 'CARRIER_SHIPMENTS_BREAKOUT'})}
                          totalValue={totalShipments.total}
                          periodLabel={totalShipments.period}
                          trendPercent={totalShipments.delta}
                          trendDirection={totalShipments.delta > 0 ? 'up' : 'down'}
                          carrierBreakout={totalShipments.carrier_breakout}
                        />
                      </div>
                      <div className='organizations-overview__metrics-wrapper'>
                        <MetricCard
                          className='w-100'
                          title={intl.formatMessage({id: 'TOTAL_SPEND'})}
                          breakoutTitle={intl.formatMessage({id: 'CARRIER_SPEND_BREAKOUT'})}
                          totalValue={totalSpend.total}
                          periodLabel={totalSpend.period}
                          trendPercent={totalSpend.delta}
                          valuePrefix='$'
                          chartColor='#14B8A6'
                          trendDirection={totalSpend.delta > 0 ? 'up' : 'down'}
                          chartValueFormat="'$'#.#a"
                          carrierBreakout={totalSpend.carrier_breakout}
                        />
                      </div>
                    </div>
                    <div className='col-12'>
                      <ShipmentTrends
                        {...analyticsFilters}
                        onExpand={() => setShowShipmentTrendsModal(true)}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className='d-flex flex-column align-items-start organizations-overview__right-side'>
                <ShipmentMap {...analyticsFilters} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {OrganizationOverview}
